/**
 * Drag-to-Scroll and Edge Auto-Scroll Functionality for Collection List Carousel
 * Provides smooth mouse and touch drag scrolling with edge detection auto-scroll
 *
 * Features:
 * - Click and drag to scroll horizontally
 * - Touch/swipe support for mobile devices
 * - Edge auto-scroll when hovering near container edges
 * - Momentum/inertia scrolling for natural feel
 * - Keyboard navigation support maintained
 * - 60fps performance optimized
 */

if (typeof window.FoxTheme === 'undefined') {
  window.FoxTheme = {};
}

FoxTheme.CollectionDragScroll = (function () {
  'use strict';

  // Conditional logging setup
  const isDev =
    window.location.hostname.includes('myshopify.com') || window.location.hostname === 'localhost';
  // eslint-disable-next-line no-console
  const log = isDev ? console.log.bind(console) : () => {};

  class DragScroll {
    constructor(element, sliderInstance) {
      this.element = element;
      this.sliderInstance = sliderInstance;
      this.slider = sliderInstance.slider;

      // Drag state
      this.isDragging = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.velocity = 0;
      this.lastX = 0;
      this.lastTimestamp = 0;

      // Edge scroll state
      this.edgeScrolling = false;
      this.edgeScrollDirection = 0;
      this.edgeScrollSpeed = 0;
      this.edgeZoneWidth = 100; // pixels from edge to trigger auto-scroll
      this.maxEdgeScrollSpeed = 15; // max pixels per frame

      // Debug mode (only enabled in development/preview environments)
      this.debugMode =
        window.location.hostname.includes('myshopify.com') ||
        window.location.hostname === 'localhost';

      // Animation frame IDs
      this.momentumRAF = null;
      this.edgeScrollRAF = null;

      // Bind methods
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleDocumentMouseMove = this.handleDocumentMouseMove.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.applyMomentum = this.applyMomentum.bind(this);
      this.performEdgeScroll = this.performEdgeScroll.bind(this);

      this.init();
    }

    init() {
      // Add drag-enabled class for styling
      this.element.classList.add('drag-enabled');

      // Mouse events
      this.element.addEventListener('mousedown', this.handleMouseDown, { passive: false });
      this.element.addEventListener('mousemove', this.handleMouseMove, { passive: false });
      this.element.addEventListener('mouseup', this.handleMouseUp);
      this.element.addEventListener('mouseleave', this.handleMouseLeave);

      // Document-level mousemove for edge scroll detection (catches events outside element bounds)
      document.addEventListener('mousemove', this.handleDocumentMouseMove, { passive: true });

      // Touch events
      this.element.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.element.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.element.addEventListener('touchend', this.handleTouchEnd, { passive: true });

      // Keyboard events (maintaining accessibility)
      this.element.addEventListener('keydown', this.handleKeyDown);

      // Prevent default drag behavior on images and links
      this.element.querySelectorAll('img, a').forEach((el) => {
        el.addEventListener('dragstart', (e) => e.preventDefault());
      });
    }

    /**
     * Mouse Event Handlers
     */
    handleMouseDown(e) {
      // Only respond to left mouse button
      if (e.button !== 0) return;

      // Don't interfere with link clicks or button clicks
      if (e.target.closest('a, button')) return;

      this.isDragging = true;
      this.element.classList.add('is-dragging');

      this.startX = e.pageX;
      this.lastX = e.pageX;
      this.lastTimestamp = Date.now();
      this.velocity = 0;

      // Get current slide position
      this.scrollLeft = this.slider.activeIndex;

      // Cancel any ongoing momentum or edge scroll
      this.cancelAnimations();

      e.preventDefault();
    }

    handleMouseMove(e) {
      // Always check for edge auto-scroll when not dragging
      if (!this.isDragging) {
        this.checkEdgeScroll(e.clientX, e.clientY);
        return;
      }

      e.preventDefault();

      const currentX = e.pageX;
      const deltaX = currentX - this.lastX;
      const currentTimestamp = Date.now();
      const deltaTime = currentTimestamp - this.lastTimestamp;

      // Calculate velocity for momentum scrolling
      if (deltaTime > 0) {
        this.velocity = deltaX / deltaTime;
      }

      // Update last position and timestamp
      this.lastX = currentX;
      this.lastTimestamp = currentTimestamp;

      // Calculate drag distance as percentage of container width
      const containerWidth = this.element.offsetWidth;
      const dragDistance = this.startX - currentX;
      const dragPercentage = dragDistance / containerWidth;

      // Determine slide change based on drag
      // More sensitive: change slide every 15% of container width
      const slideChange = Math.round(dragPercentage * 6.5);

      if (slideChange !== 0) {
        const newIndex = this.scrollLeft + slideChange;
        const maxIndex = this.slider.slides.length - 1;

        // Clamp to valid range
        const targetIndex = Math.max(0, Math.min(newIndex, maxIndex));

        if (targetIndex !== this.slider.activeIndex) {
          this.slider.slideTo(targetIndex, 0); // 0 = no animation during drag
        }
      }
    }

    handleMouseUp(_e) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.element.classList.remove('is-dragging');

      // Apply momentum scrolling if velocity is significant
      const absVelocity = Math.abs(this.velocity);
      if (absVelocity > 0.5) {
        this.applyMomentum();
      }
    }

    handleMouseLeave(e) {
      if (this.isDragging) {
        this.handleMouseUp(e);
      }
      // Stop edge scrolling when mouse leaves
      this.stopEdgeScroll();
    }

    /**
     * Document-level Mouse Move Handler
     * Handles edge scroll detection even when mouse is outside element bounds
     */
    handleDocumentMouseMove(e) {
      // Only check for edge scroll when not dragging
      if (!this.isDragging) {
        this.checkEdgeScroll(e.clientX, e.clientY);
      }
    }

    /**
     * Touch Event Handlers
     */
    handleTouchStart(e) {
      // Don't interfere with link/button touches
      if (e.target.closest('a, button')) return;

      const touch = e.touches[0];
      this.isDragging = true;
      this.element.classList.add('is-dragging');

      this.startX = touch.pageX;
      this.lastX = touch.pageX;
      this.lastTimestamp = Date.now();
      this.velocity = 0;
      this.scrollLeft = this.slider.activeIndex;

      this.cancelAnimations();
    }

    handleTouchMove(e) {
      if (!this.isDragging) return;

      e.preventDefault();

      const touch = e.touches[0];
      const currentX = touch.pageX;
      const deltaX = currentX - this.lastX;
      const currentTimestamp = Date.now();
      const deltaTime = currentTimestamp - this.lastTimestamp;

      if (deltaTime > 0) {
        this.velocity = deltaX / deltaTime;
      }

      this.lastX = currentX;
      this.lastTimestamp = currentTimestamp;

      const containerWidth = this.element.offsetWidth;
      const dragDistance = this.startX - currentX;
      const dragPercentage = dragDistance / containerWidth;
      const slideChange = Math.round(dragPercentage * 6.5);

      if (slideChange !== 0) {
        const newIndex = this.scrollLeft + slideChange;
        const maxIndex = this.slider.slides.length - 1;
        const targetIndex = Math.max(0, Math.min(newIndex, maxIndex));

        if (targetIndex !== this.slider.activeIndex) {
          this.slider.slideTo(targetIndex, 0);
        }
      }
    }

    handleTouchEnd(_e) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.element.classList.remove('is-dragging');

      const absVelocity = Math.abs(this.velocity);
      if (absVelocity > 0.5) {
        this.applyMomentum();
      }
    }

    /**
     * Keyboard Navigation Handler (Accessibility)
     */
    handleKeyDown(e) {
      // Arrow key navigation
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.slider.slidePrev(300);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.slider.slideNext(300);
      } else if (e.key === 'Home') {
        e.preventDefault();
        this.slider.slideTo(0, 300);
      } else if (e.key === 'End') {
        e.preventDefault();
        this.slider.slideTo(this.slider.slides.length - 1, 300);
      }
    }

    /**
     * Momentum Scrolling
     * Applies inertia after drag release for natural feel
     */
    applyMomentum() {
      // Skip momentum animation for users who prefer reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.velocity = 0;
        return;
      }

      const friction = 0.92; // Friction coefficient (0-1, lower = more friction)
      const minVelocity = 0.05; // Stop when velocity is very small

      const animate = () => {
        if (Math.abs(this.velocity) < minVelocity) {
          this.velocity = 0;
          return;
        }

        // Calculate slide change based on velocity
        const slideChange = Math.round(this.velocity * 10);

        if (slideChange !== 0) {
          const newIndex = this.slider.activeIndex - slideChange;
          const maxIndex = this.slider.slides.length - 1;
          const targetIndex = Math.max(0, Math.min(newIndex, maxIndex));

          this.slider.slideTo(targetIndex, 200);
        }

        // Apply friction
        this.velocity *= friction;

        // Continue animation
        this.momentumRAF = requestAnimationFrame(animate);
      };

      this.momentumRAF = requestAnimationFrame(animate);
    }

    /**
     * Edge Auto-Scroll Detection
     * Detects mouse position near ELEMENT edges (carousel edges)
     */
    checkEdgeScroll(clientX, clientY) {
      const rect = this.element.getBoundingClientRect();
      const topEdge = rect.top;
      const bottomEdge = rect.bottom;
      const leftEdge = rect.left;
      const rightEdge = rect.right;

      // First, check if mouse is within the carousel bounds
      const isWithinElement =
        clientX >= leftEdge && clientX <= rightEdge && clientY >= topEdge && clientY <= bottomEdge;

      if (!isWithinElement) {
        this.stopEdgeScroll();
        return;
      }

      // Check distance from ELEMENT edges (carousel edges)
      const distanceFromLeftEdge = clientX - leftEdge;
      const distanceFromRightEdge = rightEdge - clientX;

      if (distanceFromLeftEdge <= this.edgeZoneWidth) {
        // Near left element edge - scroll left
        if (this.debugMode) {
          log('[EdgeScroll] Near LEFT element edge - Distance:', distanceFromLeftEdge, 'px');
        }
        const speed = this.calculateEdgeScrollSpeed(distanceFromLeftEdge, true);
        this.startEdgeScroll(-1, speed);
      }
      // Check if near right element edge (within edgeZoneWidth pixels)
      else if (distanceFromRightEdge <= this.edgeZoneWidth) {
        // Near right element edge - scroll right
        if (this.debugMode) {
          log('[EdgeScroll] Near RIGHT element edge - Distance:', distanceFromRightEdge, 'px');
        }
        const speed = this.calculateEdgeScrollSpeed(distanceFromRightEdge, true);
        this.startEdgeScroll(1, speed);
      }
      // Not near any edge
      else {
        this.stopEdgeScroll();
      }
    }

    /**
     * Calculate edge scroll speed based on distance from edge
     */
    calculateEdgeScrollSpeed(distanceFromEdge, isNearEdge) {
      if (!isNearEdge) return 0;

      // Speed increases as mouse gets closer to edge
      const normalizedDistance = 1 - distanceFromEdge / this.edgeZoneWidth;
      return this.maxEdgeScrollSpeed * normalizedDistance;
    }

    /**
     * Start Edge Auto-Scroll
     */
    startEdgeScroll(direction, speed) {
      const directionText = direction > 0 ? 'RIGHT' : 'LEFT';

      // Update state
      this.edgeScrollDirection = direction;
      this.edgeScrollSpeed = speed;

      // Start scrolling if not already running
      if (!this.edgeScrolling) {
        this.edgeScrolling = true;
        if (this.debugMode) {
          log(
            `[EdgeScroll] Starting edge scroll - Direction: ${directionText}, Speed: ${speed}, Current Index: ${this.slider.activeIndex}`
          );
        }
        this.performEdgeScroll();
      } else {
        if (this.debugMode) {
          log(`[EdgeScroll] Already scrolling ${directionText} - updating speed to ${speed}`);
        }
      }
    }

    /**
     * Perform Edge Auto-Scroll Animation
     */
    performEdgeScroll() {
      if (!this.edgeScrolling) {
        return;
      }

      // Calculate target slide based on direction
      const currentIndex = this.slider.activeIndex;
      const maxIndex = this.slider.slides.length - 1;

      // Scroll right (positive direction)
      if (this.edgeScrollDirection > 0) {
        if (currentIndex < maxIndex) {
          this.slider.slideNext(300);
          // Continue scrolling after slide animation completes
          setTimeout(() => {
            this.performEdgeScroll();
          }, 350);
        } else {
          // Reached end, stop scrolling
          this.stopEdgeScroll();
        }
      }
      // Scroll left (negative direction)
      else if (this.edgeScrollDirection < 0) {
        if (currentIndex > 0) {
          this.slider.slidePrev(300);
          // Continue scrolling after slide animation completes
          setTimeout(() => {
            this.performEdgeScroll();
          }, 350);
        } else {
          // Reached beginning, stop scrolling
          this.stopEdgeScroll();
        }
      }
    }

    /**
     * Stop Edge Auto-Scroll
     */
    stopEdgeScroll() {
      if (this.debugMode && this.edgeScrolling) {
        log('[EdgeScroll] Stopping edge scroll');
      }

      this.edgeScrolling = false;
      this.edgeScrollDirection = 0;
      this.edgeScrollSpeed = 0;

      if (this.edgeScrollRAF) {
        cancelAnimationFrame(this.edgeScrollRAF);
        this.edgeScrollRAF = null;
      }
    }

    /**
     * Cancel All Animations
     */
    cancelAnimations() {
      if (this.momentumRAF) {
        cancelAnimationFrame(this.momentumRAF);
        this.momentumRAF = null;
      }
      this.stopEdgeScroll();
    }

    /**
     * Destroy and Clean Up
     */
    destroy() {
      // Remove event listeners
      this.element.removeEventListener('mousedown', this.handleMouseDown);
      this.element.removeEventListener('mousemove', this.handleMouseMove);
      this.element.removeEventListener('mouseup', this.handleMouseUp);
      this.element.removeEventListener('mouseleave', this.handleMouseLeave);
      document.removeEventListener('mousemove', this.handleDocumentMouseMove);
      this.element.removeEventListener('touchstart', this.handleTouchStart);
      this.element.removeEventListener('touchmove', this.handleTouchMove);
      this.element.removeEventListener('touchend', this.handleTouchEnd);
      this.element.removeEventListener('keydown', this.handleKeyDown);

      // Cancel animations
      this.cancelAnimations();

      // Remove classes
      this.element.classList.remove('drag-enabled', 'is-dragging');
    }
  }

  return DragScroll;
})();
